import { currencies as countriesAndCurrencies } from "@oysterhr/common"
import { countries as countriesMap } from "countries-list"
import { join, memoize, sortBy, uniqBy } from "lodash-es"
import { CurrencyOption, CurrencyWithCountryCodes } from "./types"

const currenciesByCurrencyCode: Record<string, CurrencyWithCountryCodes> =
  countriesAndCurrencies.reduce(
    (acc, c) => {
      if (acc[c.currency.code] === undefined)
        acc[c.currency.code] = { ...c.currency, countryCodes: [] }
      acc[c.currency.code].countryCodes.push(c.code)
      return acc
    },
    {} as Record<string, CurrencyWithCountryCodes>,
  )

function pickAssociatedCountryCode({ code, countryCodes }: CurrencyWithCountryCodes) {
  if (code === "EUR") {
    return "EU"
  }

  if (code === "USD") {
    return "US"
  }

  if (code === "MAD") {
    return "MA"
  }

  return countryCodes[0]
}

// TODO: Remove memoize and save the result in a variable
export const currencyInputSelectOptions = memoize<
  () => { label: string; options: CurrencyOption[] }[]
>(() => {
  const gbp = currenciesByCurrencyCode.GBP
  const usd = currenciesByCurrencyCode.USD
  const eur = currenciesByCurrencyCode.EUR
  const cad = currenciesByCurrencyCode.CAD

  const generateLabel = (c: CurrencyWithCountryCodes) => {
    const countryNamesJoined = join(
      c.countryCodes.map((code: string) => countriesMap[code as keyof typeof countriesMap]?.name),
      " ",
    )
    return `${c.code} ${c.name} ${countryNamesJoined}`
  }

  // "topCurrencyGroup" property indicates whether or not the currency appears
  // in the Top Currencies section
  return [
    {
      label: "",
      options: [
        {
          name: gbp.name,
          label: gbp.code + " " + gbp.name,
          currencyCode: gbp.code,
          countryCode: "GB",
          topCurrencyGroup: true,
        },
        {
          name: usd.name,
          currencyCode: usd.code,
          label: usd.code + " " + usd.name,
          countryCode: "US",
          topCurrencyGroup: true,
        },
        {
          name: eur.name,
          currencyCode: eur.code,
          label: eur.code + " " + eur.name,
          countryCode: "EU",
          topCurrencyGroup: true,
        },
        {
          name: cad.name,
          currencyCode: cad.code,
          label: cad.code + " " + cad.name,
          countryCode: "CA",
          topCurrencyGroup: true,
        },
      ],
    },
    {
      label: "",
      options: sortBy(Object.values(currenciesByCurrencyCode), "code")
        .filter(c => c.name)
        .map(c => {
          return {
            name: c.name,
            currencyCode: c.code,
            label: generateLabel(c),
            countryCode: pickAssociatedCountryCode(c),
            topCurrencyGroup: false,
          }
        }),
    },
  ]
})

const currencyOptionGroups = currencyInputSelectOptions()
/**
 ** The `placeholderOption` is used to divide the Top Currencies section and the rest of the currencies list
 ** Its signature is changed from `{ label: string; code: string; }` to `CurrencyOption`
 *  because of ReactSelect/TS typing issues. Since it is not critical to the functionality
 *  of this component, it is best that it shares the same typing as the other options.
 */
const placeholderOption: CurrencyOption = {
  name: "----",
  currencyCode: "---",
  label: "---",
  countryCode: "---",
}
export const currencyOptions: Array<CurrencyOption> = [
  ...currencyOptionGroups[0].options,
  ...(currencyOptionGroups[1]
    ? [placeholderOption, ...(currencyOptionGroups[1]?.options || [])]
    : []),
]

export const getCurrencies = (currencyCodes: string[]): CurrencyOption[] =>
  currencyCodes
    ? uniqBy(
        currencyOptions.filter(option => currencyCodes.includes(option.currencyCode)),
        "currencyCode",
      )
    : currencyOptions
